import api from '../api';
import { unwrapList } from '../unwrapList';
import type { TaskResponse } from './types';

// Get User Images with pagination
const getUserImages = async (filters?: { campaign_id?: number | 'null' | null, per_page?: number, page?: number, tag_ids?: number[], search?: string, status?: string, grouped?: boolean, channel?: string, scope?: 'mine' | 'team' | 'all' }): Promise<TaskResponse[]> => {
    const params = new URLSearchParams();
    if (filters?.per_page) params.append('per_page', filters.per_page.toString());
    else params.append('per_page', '30'); // Default to 30 for performance

    if (filters?.page) params.append('page', filters.page.toString());

    if (filters?.campaign_id !== undefined) {
        params.append('campaign_id', filters.campaign_id === 'null' ? 'null' : filters.campaign_id!.toString());
    }

    if (filters?.tag_ids && filters.tag_ids.length > 0) {
        filters.tag_ids.forEach(id => params.append('tag_ids[]', id.toString()));
    }

    if (filters?.search) {
        params.append('search', filters.search);
    }

    if (filters?.status && filters.status !== 'all') {
        params.append('status', filters.status);
    }

    if (filters?.grouped) {
        params.append('grouped', 'true');
    }

    if (filters?.channel && filters.channel !== 'all') {
        params.append('channel', filters.channel);
    }

    if (filters?.scope && filters.scope !== 'mine') {
        params.append('scope', filters.scope);
    }

    const response = await api.get(`/images?${params.toString()}`);
    return unwrapList<TaskResponse>(response.data);
};

// Get User Videos with pagination
const getUserVideos = async (filters?: { campaign_id?: number | 'null' | null, per_page?: number, page?: number, search?: string, status?: string, scope?: 'mine' | 'team' | 'all' }): Promise<any[]> => {
    const params = new URLSearchParams();
    if (filters?.per_page) params.append('per_page', filters.per_page.toString());
    else params.append('per_page', '30'); // Default to 30 for performance

    if (filters?.page) params.append('page', filters.page.toString());

    if (filters?.campaign_id !== undefined) {
        params.append('campaign_id', filters.campaign_id === 'null' ? 'null' : filters.campaign_id!.toString());
    }

    if (filters?.search) {
        params.append('search', filters.search);
    }

    if (filters?.status && filters.status !== 'all') {
        params.append('status', filters.status);
    }

    if (filters?.scope && filters.scope !== 'mine') {
        params.append('scope', filters.scope);
    }

    const response = await api.get(`/videos?${params.toString()}`);
    return unwrapList<any>(response.data);
};

// Fetch a single video resource (signed URL, provider, etc.) by id.
const getVideo = async (videoId: number): Promise<any> => {
    const response = await api.get(`/videos/${videoId}`);
    return (response.data as any).data;
};

// Download Image as Blob (via Proxy) with timeout and retry logic
const downloadImageBlob = async (imageId: number, retries = 3): Promise<Blob> => {
    const timeoutMs = 120000; // 2 minutes per image (4K images can be large)

    for (let attempt = 1; attempt <= retries; attempt++) {
        try {
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

            const response = await api.get(`/download/images/${imageId}/proxy`, {
                responseType: 'blob',
                signal: controller.signal
            });

            clearTimeout(timeoutId);
            return response.data as Blob;
        } catch (error: any) {
            const isLastAttempt = attempt === retries;
            const isTimeout = error.name === 'AbortError' || error.code === 'ECONNABORTED';

            console.warn(`[downloadImageBlob] Attempt ${attempt}/${retries} failed for image ${imageId}:`,
                isTimeout ? 'Timeout' : error.message);

            if (isLastAttempt) {
                throw new Error(isTimeout
                    ? `Timeout ao baixar imagem ${imageId} (tentativas esgotadas)`
                    : `Erro ao baixar imagem ${imageId}: ${error.message}`);
            }

            // Wait before retry (exponential backoff: 1s, 2s, 4s)
            await new Promise(resolve => setTimeout(resolve, 1000 * Math.pow(2, attempt - 1)));
        }
    }

    throw new Error(`Falha ao baixar imagem ${imageId} após ${retries} tentativas`);
};

// RAG context retrieval
const retrieveContext = async (prompt: string): Promise<any> => {
    const response = await api.post('/ai/retrieve-context', { prompt });
    return (response.data as any);
};

export interface BulkMediaSelection {
    image_ids?: number[];
    video_ids?: number[];
}

// NOTE: api.ts camelizes all response keys, so the API's snake_case fields
// (deleted_images, total_deleted, ...) arrive here as camelCase.
export interface BulkMediaResult {
    deletedImages?: number;
    deletedVideos?: number;
    restoredImages?: number;
    restoredVideos?: number;
    cascadedVariations: number;
    totalDeleted?: number;
    totalRestored?: number;
    denied: { type: 'image' | 'video'; id: number }[];
    notFound: { type: 'image' | 'video'; id: number }[];
}

// Bulk soft-delete (recycle bin). Deleted media leaves the gallery and every
// picker (the API hides trashed by default) and lands in the trash for 7 days.
const bulkDeleteMedia = async (selection: BulkMediaSelection): Promise<BulkMediaResult> => {
    const response = await api.post('/media/bulk-delete', selection);
    return (response.data as any).data;
};

// Lazily load the recycle bin (photos + videos from the last 7 days).
const getTrash = async (filters?: { per_page?: number }): Promise<{ images: TaskResponse[]; videos: any[] }> => {
    const params = new URLSearchParams();
    if (filters?.per_page) params.append('per_page', filters.per_page.toString());
    const response = await api.get(`/media/trash?${params.toString()}`);
    return (response.data as any).data;
};

// Restore media from the recycle bin back into the gallery.
const bulkRestoreMedia = async (selection: BulkMediaSelection): Promise<BulkMediaResult> => {
    const response = await api.post('/media/bulk-restore', selection);
    return (response.data as any).data;
};

export const galleryService = {
    getUserImages,
    getUserVideos,
    getVideo,
    downloadImageBlob,
    retrieveContext,
    bulkDeleteMedia,
    getTrash,
    bulkRestoreMedia,
};
